import React from "react";
import { X, Edit3, Save, Copy, Printer, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { STATUS_COLORS } from "@/components/tableau/utils/constants";
import { FabricPanel } from "@/components/fabric/FabricPanel";
import { useOrderFabrics } from "@/components/fabric/useOrderFabrics";
import { printFabricSheet } from "@/components/fabric/printFabricSheet";

export function CellPopupModal({
  selectedCell, setSelectedCell, orders,
  isEditing, setIsEditing, editValue, setEditValue,
  getColumnType, getColumnStatusConfig, getStatusColor,
  handleSaveCell, handleSaveCellWithValue, handleCopyContent, handlePrintContent,
  onFabricPhotosChanged, onSetManualFabrics,
}) {
  const isContent = String(selectedCell?.columnName || "").toUpperCase() === "CONTENT";
  const manualFabrics = (isContent && orders?.find(o => o.order_id === selectedCell?.orderId)?.extracted_data?._fabrics) || "";
  const fabric = useOrderFabrics(isContent ? selectedCell?.orderId : null, isEditing ? editValue : selectedCell?.content, manualFabrics);

  if (!selectedCell) return null;

  const currentOrder = orders.find(o => o.order_id === selectedCell.orderId);
  const extractedData = currentOrder?.extracted_data || {};
  const clientName = extractedData['NAME'] || extractedData['name'] || extractedData['NOM'] || '';
  const orderDate = extractedData['DATE OF ORDER'] || extractedData['DATE_OF_ORDER'] || extractedData['date_of_order'] || '';

  const displayTitle = clientName && selectedCell.columnName !== 'NAME'
    ? `${clientName} - ${selectedCell.columnName}`
    : selectedCell.columnName;

  const displayMeta = orderDate
    ? `Ligne ${selectedCell.rowIdx + 1} • Commande du ${orderDate}`
    : `Ligne ${selectedCell.rowIdx + 1}`;

  const columnType = getColumnType(selectedCell.columnName);

  const onClose = () => { setSelectedCell(null); setIsEditing(false); };

  const onPrint = isContent
    ? () => printFabricSheet({ title: displayTitle, meta: displayMeta, content: selectedCell.content || "", orderId: selectedCell.orderId, library: fabric.library, photos: fabric.photos, manual: manualFabrics })
    : handlePrintContent;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="cell-popup-overlay">
      <div className={`bg-white rounded-xl shadow-2xl ${isContent ? 'max-w-3xl max-h-[88vh]' : 'max-w-2xl max-h-[80vh]'} w-full flex flex-col`} onClick={(e) => e.stopPropagation()} data-testid="cell-popup-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div>
            <h3 className="font-semibold text-zinc-900 font-['Manrope']">{displayTitle}</h3>
            <p className="text-xs text-zinc-500">{displayMeta}</p>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing && columnType !== 'status' && (
              <button onClick={() => setIsEditing(true)} className="p-2 hover:bg-orange-100 rounded-lg transition-colors" title="Modifier" data-testid="cell-popup-edit">
                <Edit3 className="w-5 h-5 text-orange-500" />
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-lg transition-colors" data-testid="cell-popup-close">
              <X className="w-5 h-5 text-zinc-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-5">
          {(() => {
            if (columnType === 'status') {
              const config = getColumnStatusConfig(selectedCell.columnName);
              const currentColor = getStatusColor(selectedCell.content, selectedCell.columnName);
              return (
                <div className="space-y-4">
                  <p className="text-sm text-zinc-600 mb-4">Sélectionnez un statut :</p>
                  <div className="flex flex-col gap-3">
                    <button onClick={() => { setEditValue(''); setSelectedCell(prev => ({ ...prev, content: '' })); handleSaveCellWithValue(''); }} className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${!currentColor ? 'border-zinc-400 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'}`}>
                      <div className="w-5 h-5 rounded-full border-2 border-dashed border-zinc-300" />
                      <span className="text-zinc-500 italic">Vide (aucun statut)</span>
                    </button>
                    {STATUS_COLORS.map((status) => {
                      const label = config.labels[status.key] || status.label;
                      const isSelected = currentColor === status.key;
                      return (
                        <button key={status.key} onClick={() => { setEditValue(label); setSelectedCell(prev => ({ ...prev, content: label })); handleSaveCellWithValue(label); }} className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${isSelected ? 'border-orange-500 bg-orange-50' : 'border-zinc-200 hover:border-zinc-300'}`}>
                          <div className={`w-5 h-5 rounded-full ${status.color}`} />
                          <span className="font-medium">{label}</span>
                          {isSelected && <Check className="w-4 h-4 text-orange-500 ml-auto" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            }
            if (isEditing) return <Textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} className="w-full h-full min-h-[200px] resize-none text-sm font-sans" placeholder="Saisissez le contenu..." autoFocus />;
            return <pre className="whitespace-pre-wrap text-sm text-zinc-700 font-sans leading-relaxed">{selectedCell.content || <span className="text-zinc-400 italic">Cellule vide - cliquez sur le crayon pour modifier</span>}</pre>;
          })()}
          {isContent && <FabricPanel orderId={selectedCell.orderId} orderName={clientName} fabric={fabric} onPhotosChanged={onFabricPhotosChanged} manualFabrics={manualFabrics} onSetManualFabrics={onSetManualFabrics} />}
        </div>

        {/* Footer */}
        {columnType !== 'status' && (
          <div className="flex items-center gap-3 px-5 py-4 border-t border-zinc-200 bg-zinc-50 rounded-b-xl">
            <Button variant="outline" size="sm" onClick={handleCopyContent} className="gap-2" data-testid="cell-popup-copy"><Copy className="w-4 h-4" />Copier</Button>
            <Button variant="outline" size="sm" onClick={onPrint} className="gap-2" data-testid="cell-popup-print"><Printer className="w-4 h-4" />Imprimer</Button>
            <div className="flex-1" />
            {isEditing ? (
              <>
                <Button variant="outline" size="sm" onClick={() => { setEditValue(selectedCell.content); setIsEditing(false); }} data-testid="cell-popup-cancel">Annuler</Button>
                <Button size="sm" onClick={handleSaveCell} className="gap-2 bg-orange-500 hover:bg-orange-600" data-testid="cell-popup-save"><Save className="w-4 h-4" />Enregistrer</Button>
              </>
            ) : (
              <Button variant="default" size="sm" onClick={onClose} className="bg-orange-500 hover:bg-orange-600" data-testid="cell-popup-footer-close">Fermer</Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
